import { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { TrackEventGTM } from '../../utils/filters/analyticsUtils';
import FormInputSearch from '../FormInputSearch/FormInputSearch';
import './RecruitSessionDropdown.scss';
import ScienceIcon from '../../assets/sidebar/icons/ScienceIcon';
import { t } from '../../i18n/i18n';
import { CREATE_EVALUATION, SCREENING_SEARCHES } from '../../constants/routes';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { defaultColorCompany } from '../../constants/colors';
import DebouncedButton from '../Button/Button';
import Spinner from '../spinner/Spinner';
import { EventsGTM } from '../../constants/events';

const RecruitSessionDropdown = ({
  options,
  className,
  setOpenDropdown,
  openDropdown,
  disabled = false,
  createAssessmentInvitationAction,
  isLoading = false,
}) => {
  const [searchRecruitSessionStages, setSearchRecruitSessionStages] = useState('');
  const [recruitSessionStagesFiltered, setRecruitSessionStagesFiltered] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const history = useHistory();
  const dropdownRef = useRef();
  const listRef = useRef();

  useEffect(() => {
    setRecruitSessionStagesFiltered(options);
  }, [options]);

  useEffect(() => {
    if (searchRecruitSessionStages.length > 0) {
      const filterData = options.filter((recruit) =>
        recruit.name.toLowerCase().includes(searchRecruitSessionStages.toLowerCase())
      );
      setRecruitSessionStagesFiltered(filterData);
      setSelectedIndex(-1);
    } else {
      setRecruitSessionStagesFiltered(options);
      setSelectedIndex(-1);
    }
  }, [searchRecruitSessionStages, options]);

  const handleClick = useCallback(
    (item) => {
      const body = {
        id: item?.id,
        levelId: item?.levelId,
        name: item?.name,
        technologyId: item?.technologyId,
      };
      createAssessmentInvitationAction(body);
      setOpenDropdown(false);
      setSearchRecruitSessionStages('');
    },
    [createAssessmentInvitationAction, setOpenDropdown]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!openDropdown) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < recruitSessionStagesFiltered.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < recruitSessionStagesFiltered.length) {
            handleClick(recruitSessionStagesFiltered[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpenDropdown(false);
          setSearchRecruitSessionStages('');
          break;
        default:
          break;
      }
    },
    [openDropdown, selectedIndex, recruitSessionStagesFiltered, handleClick, setOpenDropdown]
  );

  useEffect(() => {
    if (openDropdown) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [openDropdown, handleKeyDown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(false);
        setSearchRecruitSessionStages('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setOpenDropdown]);

  return (
    !disabled && (
      <div ref={dropdownRef} className="recruit-session-dropdown-container position-relative">
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{t('SELECT_CANDIDATE_TO_EVALUATE_SHORTCUT')}</Tooltip>}
        >
          <button
            onClick={() => {
              if (!disabled) {
                setOpenDropdown(!openDropdown);
                setSelectedIndex(-1);
              }
            }}
            className={`p-2 rounded border-0 bg-transparent d-flex align-items-center justify-content-center ${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            } ${openDropdown ? 'active' : ''}`}
            disabled={disabled}
            aria-label={t('SELECT_CANDIDATE_TO_EVALUATE_SHORTCUT')}
            aria-expanded={openDropdown}
            aria-haspopup="true"
          >
            <ScienceIcon
              width="20"
              height="20"
              color={disabled ? '#828282' : openDropdown ? '#2563eb' : defaultColorCompany}
            />
          </button>
        </OverlayTrigger>

        {openDropdown && (
          <div
            className={`dropdown-menu-container position-absolute bg-white rounded shadow-lg overflow-hidden ${className}`}
            role="menu"
            aria-labelledby="dropdown-trigger"
            ref={listRef}
          >
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center p-4">
                <Spinner show />
              </div>
            ) : (
              <>
                {recruitSessionStagesFiltered?.length > 0 ? (
                  <>
                    <div className="search-container-dropdown p-2 border-bottom bg-light">
                      <FormInputSearch
                        searchText={searchRecruitSessionStages}
                        setSearchText={setSearchRecruitSessionStages}
                        placeholder={t('EVALUATION_PLACEHOLDER')}
                        className="w-100"
                        autoFocus
                      />
                    </div>

                    <div className="options-container overflow-auto" style={{ maxHeight: '250px' }}>
                      {recruitSessionStagesFiltered.map((item, index) => (
                        <div
                          key={item.id}
                          className={`dropdown-item-container ${
                            selectedIndex === index ? 'selected-item' : ''
                          }`}
                          onMouseEnter={() => setSelectedIndex(index)}
                          role="menuitem"
                        >
                          <DebouncedButton
                            className="dropdown-item-btn w-100 text-left p-3 border-0 bg-transparent d-flex align-items-center"
                            onClick={() => handleClick(item)}
                            type="button"
                          >
                            <span className="item-number text-muted mr-3">{index + 1}.</span>
                            <span className="item-name flex-grow-1 text-truncate">{item.name}</span>
                          </DebouncedButton>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state p-4 text-center">
                    <p className="text-muted mb-3">
                      {searchRecruitSessionStages
                        ? t('NO_RESULTS_FOUND')
                        : t('NON_EVALUATIONS_TO_SELECT_SHORTCUT')}
                    </p>
                    {!searchRecruitSessionStages && (
                      <button
                        data-testid="button-create-search"
                        className="btn btn-primary btn-sm rounded-pill px-3 py-1"
                        type="button"
                        onClick={() => {
                          TrackEventGTM(EventsGTM.CREATE_EVALUATION, {
                            event_category:
                              'Create evaluation from send candidate to another evaluation',
                            event_action: 'click',
                          });
                          setOpenDropdown(false);
                          history.push({
                            pathname: CREATE_EVALUATION,
                            state: {
                              from: SCREENING_SEARCHES,
                              fromName: 'MENU_ITEM_SEARCHES',
                            },
                          });
                        }}
                      >
                        {t('CREATE_SEARCH')}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    )
  );
};

export default RecruitSessionDropdown;
